import { format } from "date-fns";
import type { EntregaDoPedido, EtiquetaDeEntrega, EtiquetaDeProducao } from "@bookdental/schemas";
import { formatCep } from "@/lib/viacep";
import { codigoDoPedido, codigoDoServico } from "@/lib/codigo-do-pedido";
import { abrirImpressao, esc } from "./impressao";
import { qrDoCatalogo } from "./etiqueta-unica";

export type PedidoDaEtiqueta = {
  id: string;
  numero?: number | null;
  paciente: string;
  dentista: string | null;
  clinica: string | null;
  prazo: Date | string | null;
  criadoEm?: Date | string | null;
};

export type ServicoDaEtiqueta = {
  ordem?: number | null;
  servico: string;
  material: string | null;
  dentes: string | null;
  cor: string | null;
  observacao?: string | null;
  etapa?: string | null;
  tecnico?: string | null;
};

export type Remetente = { nome: string; cidade?: string | null; uf?: string | null; telefone?: string | null };
export type Pagina = { css: string; corpo: string };

/** Medidas de cada tamanho, em mm, e quanto a letra cresce ou encolhe nele. */
const MEDIDAS = {
  "60x40": { l: 60, a: 40, escala: 1 },
  "50x30": { l: 50, a: 30, escala: 0.82 },
  "100x50": { l: 100, a: 50, escala: 1.3 },
  a4: { l: 63.5, a: 38.1, escala: 1 },
} as const;
const FONTE = { compacta: 0.88, normal: 1, grande: 1.16 } as const;

/**
 * Térmica: uma etiqueta por página do tamanho escolhido. A4: folha com grade 3×7 de 63,5×38,1 mm
 * (padrão das folhas adesivas). Linha que não cabe é cortada com reticências — a etiqueta nunca
 * cresce pra uma segunda.
 *
 * Desenho de ficha: a primeira linha é sempre o destaque, na largura toda; embaixo, duas colunas com
 * rótulo pequeno em cima e valor embaixo — a da direita, mais estreita, com os valores curtos. Na de entrega, rótulo à
 * esquerda e valor à direita, em faixas.
 */
function css(tamanho: keyof typeof MEDIDAS, fonte: keyof typeof FONTE) {
  const m = MEDIDAS[tamanho];
  const f = m.escala * FONTE[fonte];
  const pt = (n: number) => `${(n * f).toFixed(2)}pt`;
  const pagina =
    tamanho === "a4"
      ? `@page { size: A4; margin: 10.7mm 7.2mm; } .folha { display: grid; grid-template-columns: repeat(3, ${m.l}mm); grid-auto-rows: ${m.a}mm; column-gap: 2.5mm; } .etq { break-inside: avoid; }`
      : `@page { size: ${m.l}mm ${m.a}mm; margin: 0; } .etq { break-after: page; } .etq:last-child { break-after: auto; }`;
  return `${pagina}
.etq { width: ${m.l}mm; height: ${m.a}mm; padding: 1.6mm 2.2mm; overflow: hidden; display: flex; flex-direction: column; border: 0.35mm solid #000; border-radius: 1.6mm; }
.rot { font-size: ${pt(4.8)}; font-weight: 700; letter-spacing: 0.2pt; text-transform: uppercase; color: #333; line-height: 1.1; }
.v { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: ${pt(7.3)}; font-weight: 700; line-height: 1.15; text-transform: uppercase; }
.campo { padding: 0.25mm 0 0.35mm; border-bottom: 0.2mm solid #000; min-width: 0; }
.campo:last-child { border-bottom: 0; }
.destaque { font-size: ${pt(14)}; font-weight: 800; line-height: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.topo { display: flex; justify-content: space-between; align-items: flex-end; gap: 1.5mm; border-bottom-width: 0.3mm; }
.min-w { min-width: 0; }
.grade { display: grid; grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr); column-gap: 2mm; align-content: start; }
.grade .campo:last-child, .grade .campo:nth-last-child(2) { border-bottom: 0; }
.vazio { min-width: 0; }
.logo { max-height: 6mm; max-width: 16mm; object-fit: contain; align-self: flex-end; }
.etq { position: relative; }
.qrcat { position: absolute; right: 2.2mm; bottom: 1.4mm; width: 13mm; text-align: center; background: #fff; }
.qrcat svg { width: 13mm; height: 13mm; display: block; }
.qrcat span { font-size: 4.2pt; font-weight: 700; text-transform: uppercase; }
.faixas { display: grid; grid-template-columns: ${(m.l * 0.19).toFixed(1)}mm 1fr; column-gap: 1.6mm; row-gap: 0.4mm; align-items: baseline; }
.faixas .v { text-transform: none; }
.campo.dentes .v { white-space: normal; }
.faixas .rot { white-space: nowrap; }
.pe { margin-top: auto; border-top: 0.3mm solid #000; padding-top: 0.6mm; }
@media screen { body { background: #e4e4e7; padding: 8px; } .etq { background: #fff; margin: 0 auto 8px; } .folha .etq { margin: 0 0 2.5mm; } }`;
}

const data = (d: Date | string | null | undefined) => (d ? format(new Date(d), "dd/MM/yyyy") : "—");
const juntar = (...partes: (string | null | undefined)[]) => partes.filter(Boolean).join(" · ");

/** Valores curtos vão pra coluna da direita, mais estreita. Cada campo tem a sua linha — juntar dois
 *  num campo só cortava os dois. */
const DA_DIREITA = new Set(["cor", "prazo", "dentes", "dataDoPedido", "etapa", "tecnico"]);

function valoresDaEtiqueta(p: PedidoDaEtiqueta, s: ServicoDaEtiqueta): Record<string, [string, string | null | undefined]> {
  return {
    codigo: [`Pedido ${codigoDoPedido(p)}`, codigoDoServico(p, s)],
    paciente: ["Paciente", p.paciente],
    dentista: ["Dentista", p.dentista],
    clinica: ["Clínica", p.clinica],
    servico: ["Serviço", s.servico],
    material: ["Material", s.material],
    dentes: ["Dentes", s.dentes],
    cor: ["Cor", s.cor],
    prazo: ["Prazo", data(p.prazo)],
    dataDoPedido: ["Pedido em", data(p.criadoEm)],
    observacao: ["Observação", s.observacao],
    etapa: ["Etapa", s.etapa],
    tecnico: ["Técnico", s.tecnico],
  };
}

/**
 * Primeira linha: o destaque (código ou qualquer campo), na largura toda. Embaixo, duas colunas em
 * grade: a linha N da esquerda e a da direita têm a mesma altura e o mesmo traço — as linhas ficam
 * alinhadas de coluna a coluna. O campo do destaque não se repete embaixo; com outro destaque, o
 * código do serviço entra na coluna da direita — a peça nunca fica sem ele.
 */
function etiquetaDeProducao(cfg: EtiquetaDeProducao, p: PedidoDaEtiqueta, s: ServicoDaEtiqueta, logo?: string | null) {
  const valor = valoresDaEtiqueta(p, s);
  const [rotulo, destaque] = valor[cfg.destaque];
  const ligados = cfg.campos
    .filter((c) => c.ligado && c.chave !== cfg.destaque && (c.chave !== "observacao" || s.observacao))
    .map((c) => c.chave as string);
  const esq = ligados.filter((c) => !DA_DIREITA.has(c));
  const dir = [...(cfg.destaque === "codigo" ? [] : ["codigo"]), ...ligados.filter((c) => DA_DIREITA.has(c))];
  const celula = (c: string | undefined, lado: "e" | "d") =>
    c
      ? `<div class="campo ${lado} ${c}"><div class="rot">${esc(c === "codigo" ? "Código" : valor[c][0])}</div><div class="v">${esc(valor[c][1] || "—")}</div></div>`
      : `<div class="vazio"></div>`;
  const linhas = Array.from({ length: Math.max(esq.length, dir.length) }, (_, i) => celula(esq[i], "e") + celula(dir[i], "d")).join("");
  const logoHtml = cfg.logo && logo ? `<img class="logo" src="${esc(logo)}" alt="">` : "";
  return `<section class="etq">
  <div class="campo topo"><div class="min-w"><div class="rot">${esc(rotulo)}</div><div class="destaque">${esc(destaque || "—")}</div></div>${logoHtml}</div>
  <div class="grade">${linhas}</div>
</section>`;
}

/** Uma etiqueta por serviço — vai com o trabalho pela bancada. */
export function paginaDeProducao(cfg: EtiquetaDeProducao, pedido: PedidoDaEtiqueta, servicos: ServicoDaEtiqueta[], logo?: string | null): Pagina {
  const etiquetas = servicos.map((s) => etiquetaDeProducao(cfg, pedido, s, logo)).join("");
  return { css: css(cfg.tamanho, cfg.fonte), corpo: cfg.tamanho === "a4" ? `<div class="folha">${etiquetas}</div>` : etiquetas };
}

const faixa = (rotulo: string, valor: string) => `<div class="rot">${esc(rotulo)}</div><div class="v">${valor}</div>`;

/** Uma por pedido — vai na caixa, com destinatário, remetente e os códigos dos trabalhos. */
export function paginaDeEntrega(
  cfg: EtiquetaDeEntrega,
  pedido: PedidoDaEtiqueta,
  servicos: ServicoDaEtiqueta[],
  destino: EntregaDoPedido,
  remetente: Remetente,
  logo?: string | null,
  /** Link público do catálogo; nulo (catálogo desligado) = sem QR, mesmo marcado. */
  urlDoCatalogo?: string | null,
): Pagina {
  const on = new Set(cfg.campos.filter((c) => c.ligado).map((c) => c.chave));
  const rua = `${destino.endereco}, ${destino.numero}${destino.complemento ? ` — ${destino.complemento}` : ""}`;
  const endereco = `${esc(rua)}<br>${esc(juntar(destino.bairro, `${destino.cidade}/${destino.uf}`))}`;
  const de = juntar(remetente.nome, remetente.cidade && remetente.uf ? `${remetente.cidade}/${remetente.uf}` : null, remetente.telefone);
  const pe = [
    on.has("pedido") ? faixa("Pedido", `<b>${esc(codigoDoPedido(pedido))}</b>`) : "",
    on.has("paciente") ? faixa("Paciente", esc(pedido.paciente)) : "",
    on.has("dentista") ? faixa("Dentista", esc(pedido.dentista || "—")) : "",
    on.has("trabalhos") ? faixa("Trabalhos", esc(servicos.map((s) => codigoDoServico(pedido, s)).join(", ") || "—")) : "",
  ].join("");
  const logoHtml = cfg.logo && logo ? `<img class="logo" src="${esc(logo)}" alt="">` : "";
  const etq = `<section class="etq">
  <div class="faixas">
    ${faixa("Para", `<b>${esc(destino.destinatario)}</b>`)}
    ${faixa("Endereço", endereco)}
    ${faixa("CEP", esc(juntar(formatCep(destino.cep), on.has("telefone") && destino.telefone ? `Tel. ${destino.telefone}` : null)))}
    ${on.has("remetente") ? faixa("De", esc(de)) : ""}
  </div>
  ${pe ? `<div class="pe faixas">${pe}</div>` : ""}${logoHtml}${cfg.qrCatalogo && urlDoCatalogo ? qrDoCatalogo(urlDoCatalogo) : ""}
</section>`;
  return { css: css(cfg.tamanho, cfg.fonte), corpo: cfg.tamanho === "a4" ? `<div class="folha">${etq}</div>` : etq };
}

/** Manda a página pra impressão; `false` = o navegador bloqueou a janela. */
export function imprimirPagina(titulo: string, pagina: Pagina) {
  const janela = abrirImpressao(460, 600);
  if (!janela) return false;
  janela.escrever(titulo, pagina.css, pagina.corpo);
  return true;
}

/** A mesma página como documento, pra prévia num iframe (sem mandar imprimir). */
export function documentoDaPrevia(pagina: Pagina) {
  return `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><style>* { box-sizing: border-box; } body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; } ${pagina.css}</style></head><body>${pagina.corpo}</body></html>`;
}
